import type { ActivityCatalogEntry, HistorySnapshot } from '../types'
import { CATEGORY_STYLES } from '../lib/categoryColors'

interface Props {
  snapshots: HistorySnapshot[]
  catalog: ActivityCatalogEntry[]
}

export default function DecisionHistoryList({ snapshots, catalog }: Props) {
  const byId = new Map(catalog.map((a) => [a.id, a]))
  return (
    <div className="card">
      <h2 className="text-base font-semibold mb-3">Decision history</h2>
      <ol className="space-y-3">
        {snapshots.map((snap) => (
          <DecisionRow
            key={snap.quarter}
            snap={snap}
            byId={byId}
            boardOutcome={snap.board_request_outcome}
          />
        ))}
      </ol>
    </div>
  )
}

export function DecisionRow({
  snap, byId, boardOutcome,
}: {
  snap: HistorySnapshot
  byId: Map<string, ActivityCatalogEntry>
  boardOutcome: 'granted' | 'denied' | null
}) {
  const d = snap.decision
  const starts = d?.programs_to_start ?? []
  const stops = d?.programs_to_stop ?? []
  const oneShots = d?.one_shots ?? []
  const tempo = d?.tempo ?? 1
  const boardRequest = d?.request_board_budget_increase ?? false
  const noChanges = starts.length === 0 && stops.length === 0 && oneShots.length === 0
                 && !boardRequest && tempo === 1

  return (
    <li className="border-l-2 border-slate-200 pl-3">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-xs font-mono font-semibold text-slate-700">Q{snap.quarter}</span>
        <span className="text-xs text-slate-500">
          Urgency {tempo.toFixed(1)}× · ${snap.budget_spent_k.toFixed(0)}K spent
        </span>
      </div>
      {noChanges ? (
        <p className="text-xs text-slate-400 italic">Coasted — no program changes.</p>
      ) : (
        <div className="space-y-1">
          {starts.length > 0 && <PillRow label="Started" ids={starts} byId={byId} tone="start" />}
          {stops.length > 0 && <PillRow label="Stopped" ids={stops} byId={byId} tone="stop" />}
          {oneShots.length > 0 && <PillRow label="One-shots" ids={oneShots} byId={byId} tone="oneshot" />}
          {boardRequest && (
            <p className="text-xs text-slate-600 flex items-center gap-2 flex-wrap">
              <span className="font-medium">Board budget request</span>
              {boardOutcome === 'granted' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                  Granted · +25% budget for 4q
                </span>
              )}
              {boardOutcome === 'denied' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-medium">
                  Denied · −8 Confidence
                </span>
              )}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

function PillRow({
  label, ids, byId, tone,
}: {
  label: string
  ids: string[]
  byId: Map<string, ActivityCatalogEntry>
  tone: 'start' | 'stop' | 'oneshot'
}) {
  const toneClass =
    tone === 'start' ? 'bg-brand-50 border-brand-200 text-brand-800'
    : tone === 'stop' ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-amber-50 border-amber-200 text-amber-800'
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-xs text-slate-500 w-20 shrink-0">{label}</span>
      {ids.map((id) => {
        const a = byId.get(id)
        const dotClass = a ? CATEGORY_STYLES[a.category].dot : 'bg-slate-300'
        const name = a?.name ?? id
        return (
          <span
            key={id}
            title={name}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${toneClass}`}
          >
            <span aria-hidden className={`inline-block w-2 h-2 rounded-full ${dotClass}`} />
            <span className="font-mono">{id}</span>
            <span className="text-slate-500 font-normal">· {name}</span>
          </span>
        )
      })}
    </div>
  )
}
